import "./styles.scss";

import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Text,
  Table,
  StatusChip,
  Input,
  Popper,
  Button,
  Icon,
} from "@fragments";
import TabsSelector from "@components/TabsSelector";
import {
  IconClipboardList,
  IconFilePlus,
  IconCopy,
  IconSearch,
  IconSettings,
  IconTrash,
  IconDots,
  IconDownload,
  IconPencil,
  IconShare,
  IconForms,
  IconClipboardText,
  IconPlus,
} from "@tabler/icons-react";

import topBubbles from "@assets/backgrounds/top-bubbles.svg";

import useConfigHeader from "@pages/CommunityConfig/hooks/useConfigHeader";

export default function FormsView({
  forms,
  loading,
  page,
  pages,
  records,
  search,
  handleSearch,
  handleSelectPage,
  onCreateFromScratch,
  onUseTemplate,
  onEditForm,
  onDuplicateForm,
  onToggleActive,
  onDeleteForm,
  subTabs,
  activeSubTab,
  activeComponent,
  onSubTabChange,
}) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { slug } = useParams();
  const popperRef = useRef();

  // Page identity lives in the floating settings header. It stays for both
  // sub-tabs (list / submissions), as the in-page heading did.
  useConfigHeader({
    title: t("forms.list.title"),
    subtitle: t("forms.list.subtitle"),
    icon: <IconClipboardText />,
  });

  function openMenu(event, item) {
    popperRef.current.open(
      [
        {
          id: "edit",
          label: t("forms.list.menu.edit"),
          icon: <IconPencil size={15} />,
          onClick: async () => onEditForm?.(item),
        },
        {
          id: "duplicate",
          label: t("forms.list.menu.duplicate"),
          icon: <IconCopy size={15} />,
          onClick: async () => onDuplicateForm?.(item),
        },
        {
          id: "download",
          label: t("forms.list.menu.download"),
          icon: <IconDownload size={15} />,
          onClick: async () => downloadForm(item),
        },
        { divider: true, id: "divider-1" },
        {
          id: "delete",
          label: t("forms.list.menu.delete"),
          icon: <IconTrash size={15} />,
          variant: "danger",
          onClick: async () => confirmDelete(item),
        },
      ],
      "bottom",
      event.currentTarget,
    );
  }

  function confirmDelete(item) {
    window.openConfirm({
      title: t("forms.list.delete.title"),
      headline: t("shared.warning"),
      subtitle: t("forms.list.delete.subtitle", { title: item.title }),
      buttons: [
        {
          text: t("forms.list.delete.confirm"),
          onClick: () => onDeleteForm?.(item),
          variant: "contained",
          color: "error",
          className: "",
        },
        {
          text: t("common:actions.cancel"),
          variant: "outlined",
          className: "",
        },
      ],
    });
  }

  function downloadForm(item) {
    const { id, community_id, return_url, banner, ...templateData } = item;

    const cleanedData = {
      ...templateData,
      actions: [],
      steps: (templateData.steps ?? []).map(({ id: _sid, ...step }) => ({
        ...step,
        questions: (step.questions ?? []).map(
          ({ id: _qid, ...question }) => question,
        ),
      })),
    };

    const sanitizedTitle = (item.title || "formulario")
      .replace(/[\\/:*?"<>|]/g, "")
      .trim();
    const filename = `${sanitizedTitle} - Template.json`;
    const blob = new Blob([JSON.stringify(cleanedData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const columns = [
    {
      id: "title",
      title: t("forms.list.columns.form"),
      grow: 1,
      customRender: (item) => (
        <div className="form-title-cell">
          <div className="form-title-icon">
            <IconClipboardList size={16} />
          </div>
          <Text.span contrast={8}>{item.title || "—"}</Text.span>
        </div>
      ),
    },
    {
      id: "answers_count",
      title: t("forms.list.columns.answers"),
      width: "100px",
      align: "center",
      customRender: (item) => (
        <Text.span contrast={5}>{String(item.answers_count ?? 0)}</Text.span>
      ),
    },
    {
      id: "return_url",
      title: t("forms.list.columns.returnUrl"),
      width: "220px",
      customRender: (item) =>
        item.return_url ? (
          <Text.span contrast={5} className="return-url-cell">
            {item.return_url}
          </Text.span>
        ) : (
          <Text.span contrast={3}>—</Text.span>
        ),
    },
  ];

  return (
    <div className="forms-tab px-4">
      <div className="mt-4 mb-3">
        <TabsSelector
          tabs={subTabs}
          value={activeSubTab}
          onChange={onSubTabChange}
        />
      </div>

      {activeSubTab === "list" ? (
        <>
          {/* ── Creation cards ─────────────────────────────────── */}
          <div className="forms-creation-row">
            <CreationCard
              icon={<IconFilePlus size={22} />}
              title={t("forms.list.create.scratchTitle")}
              description={t("forms.list.create.scratchDescription")}
              buttonText={t("forms.list.create.scratchButton")}
              buttonColor="inverse"
              onClick={onCreateFromScratch}
            />
            <CreationCard
              buttonColor="inverse"
              icon={<IconCopy size={22} />}
              title={t("forms.list.create.templateTitle")}
              description={t("forms.list.create.templateDescription")}
              buttonText={t("forms.list.create.templateButton")}
              onClick={onUseTemplate}
            />
            <SearchCard onClick={onUseTemplate} />
          </div>

          {/* ── Forms table ────────────────────────────────────── */}
          <div className="forms-table-section">
            <div className="forms-table-header">
              <Text.h5 contrast={7}>
                {loading
                  ? t("common:actions.loading")
                  : t("forms.list.count", { count: records })}
              </Text.h5>
            </div>

            <Table
              id="community-forms-table"
              columns={columns}
              data={forms}
              loading={loading}
              loadingRows={5}
              enablePagination={pages > 1}
              page={page}
              pages={pages}
              records={records}
              contained={true}
              onSelectPage={handleSelectPage}
              emptyMessage={t("forms.list.emptyMessage")}
              emptySubtitle={t("forms.list.emptySubtitle")}
              emptyIcon={<IconClipboardList size={36} />}
              actionsComponent={(item) => (
                <div className="form-row-actions">
                  <Button
                    size="small"
                    color="overlay"
                    startIcon={<IconClipboardText size={16} />}
                    onClick={() =>
                      navigate(
                        `/${slug}/settings/forms/submissions?form_id=${item.id}`,
                      )
                    }
                  >
                    {t("forms.list.viewSubmissions")}
                  </Button>
                  <Button
                    size="small"
                    color="overlay"
                    startIcon={<IconShare />}
                    onClick={() => {
                      const link = `https://forms.ginastee.com/${item.id}`;
                      navigator.clipboard.writeText(link).then(() => {
                        window.setSnackbar?.(t("forms.list.linkCopied"));
                      });
                    }}
                  >
                    {t("common:actions.share")}
                  </Button>
                  <Input.Switch
                    value={!!item.active}
                    onInput={(val) => onToggleActive?.(item, val)}
                    size="small"
                    className="toggle-form-state-table"
                    label={!!item.active ? "Ativo" : "Inativo"}
                  />
                  <Button
                    size="small"
                    color="text"
                    icon={<IconSettings size={16} />}
                    onClick={() => onEditForm?.(item)}
                  />
                  <Button
                    size="small"
                    color="text"
                    icon={<IconDots size={16} />}
                    onMouseEnter={(e) => openMenu(e, item)}
                    onClick={(e) => openMenu(e, item)}
                  />
                </div>
              )}
            />
            <Popper controlRef={popperRef} config={{ activateOnHover: true }} />
          </div>
        </>
      ) : (
        activeComponent
      )}
    </div>
  );
}

function CreationCard({
  icon,
  title,
  description,
  buttonText,
  buttonColor = "overlay",
  highlightColor = "primary",
  disabled,
  onClick,
}) {
  return (
    <div
      className={`forms-creation-card${disabled ? " forms-creation-card--disabled" : ""}`}
    >
      <img src={topBubbles} alt="" aria-hidden="true" className="fcc-bg" />
      <Icon.Sphere size={58}>{icon}</Icon.Sphere>
      <div className="fcc-body">
        <Text.h5 weight={500} contrast={7}>{title}</Text.h5>
        <Text.span contrast={5}>{description}</Text.span>
      </div>
      <Button
        variant="rounded"
        color={buttonColor}
        endIcon={<IconPlus />}
        highlightColor={highlightColor}
        fullWidth
        disabled={disabled}
        onClick={onClick}
      >
        {buttonText}
      </Button>
    </div>
  );
}

function SearchCard({ onClick }) {
  const { t } = useTranslation("settings");

  return (
    <div className="forms-creation-card">
      <img src={topBubbles} alt="" aria-hidden="true" className="fcc-bg" />
      <Icon.Sphere size={58}>
        <IconSearch size={22} />
      </Icon.Sphere>
      <div className="fcc-body">
        <Text.h5 weight={500} contrast={7}>{t("forms.list.create.exploreTitle")}</Text.h5>
        <Text.span contrast={5}>
          {t("forms.list.create.exploreDescription")}
        </Text.span>
      </div>
      <Button
        variant="rounded"
        color="inverse"
        endIcon={<IconSearch size={22} />}
        highlightColor="primary"
        fullWidth
        onClick={onClick}
      >
        {t("forms.list.create.exploreButton")}
      </Button>
    </div>
  );
}
